import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { themeApi } from '@/api/theme.api';
import { rememberCollectorModuleContribution } from '@/lib/community-collector.storage';
import type { resolveCollectorContributionContext } from '@/lib/community-collector.storage';
import { loadAudioDrafts, saveAudioDrafts } from '../audio-drafts.storage';
import {
  audioContributionPayload,
  audioSubmitBlock,
  collectorAudioEntry,
  submitErrorResult,
  type SubmitResult,
} from '../initial-data.payloads';
import type { AudioAnswer } from '../types';
import { useAudioRecorder } from './use-audio-recorder';
import { useAudioQuality } from './use-audio-quality';
import { useInitialDataCatalog } from './use-initial-data-catalog';
import { useInitialDataSetup } from './use-initial-data-setup';
import { useQuestionSession } from './use-question-session';
import { useSessionPause } from './use-session-pause';

type CollectorContext = ReturnType<typeof resolveCollectorContributionContext>;

export type { SubmitResult };

/** Setup, one-question-at-a-time recording, drafts and submission for /initial-audio. */
export function useInitialAudioSession(collectorContext: CollectorContext) {
  const setup = useInitialDataSetup();
  const catalog = useInitialDataCatalog(setup.sectorId, setup.language, { trackSubmitted: true });
  const session = useQuestionSession(catalog.questions, catalog.submittedQuestionIds);
  const recorder = useAudioRecorder();
  const quality = useAudioQuality(recorder.answer?.blob ?? null);
  const pause = useSessionPause({ mode: 'audio', setup, catalog, session });
  const queryClient = useQueryClient();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const draftsRef = useRef<Record<string, AudioAnswer>>({});
  // Bumped when drafts finish loading, so the current question picks its draft up.
  const [draftsVersion, setDraftsVersion] = useState(0);

  // Drafts of the chosen sector and language, loaded once per pair.
  useEffect(() => {
    draftsRef.current = {};
    if (!setup.sectorId || !setup.language) return;
    let cancelled = false;
    void loadAudioDrafts(setup.sectorId, setup.language).then((drafts) => {
      if (cancelled) return;
      draftsRef.current = drafts;
      setDraftsVersion((version) => version + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [setup.sectorId, setup.language]);

  const currentId = session.current?.id;
  const { load } = recorder;

  // Show the draft of the question on screen, or an empty recorder.
  useEffect(() => {
    load(currentId ? draftsRef.current[currentId] ?? null : null);
  }, [currentId, load, draftsVersion]);

  const persistDraft = useCallback(
    (questionId: string, answer: AudioAnswer | null) => {
      const next = { ...draftsRef.current };
      if (answer) next[questionId] = answer;
      else delete next[questionId];
      draftsRef.current = next;
      void saveAudioDrafts(setup.sectorId, setup.language, next);
    },
    [setup.sectorId, setup.language],
  );

  // Keep every new recording as a draft of the question it was started on. The
  // recorder's answer state lags one render behind a question change, so saving
  // from an effect on [currentId, answer] wrote the previous answer to the next question.
  const startRecording = () => {
    if (!currentId) return;
    const questionId = currentId;
    void recorder.start((answer) => persistDraft(questionId, answer));
  };

  const startSession = () => {
    pause.resetCarryOver();
    session.start();
    setup.setStep('session');
  };

  const submitCurrent = async (): Promise<SubmitResult> => {
    const question = session.current;
    const blocked = audioSubmitBlock(Boolean(question), recorder.answer, quality);
    if (blocked || !question || !recorder.answer) return blocked ?? { ok: false };
    setIsSubmitting(true);
    try {
      await themeApi.createContributionWithAudioFiles(
        audioContributionPayload({
          sectorId: setup.sectorId,
          language: setup.language,
          question,
          collectorContext,
          collectionPlace: setup.collectionPlace.place,
          answer: recorder.answer,
          quality,
          recordingDevice: setup.recordingDevice,
        }),
      );
      rememberCollectorModuleContribution(collectorContext, collectorAudioEntry(question));
      persistDraft(question.id, null);
      session.markSubmitted(question.id);
      void queryClient.invalidateQueries({ queryKey: ['initial-data', 'submitted', setup.sectorId, setup.language] });
      return { ok: true };
    } catch (error) {
      return submitErrorResult(error, true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const discardRecording = () => {
    if (currentId) persistDraft(currentId, null);
    recorder.reset();
  };

  return {
    setup,
    // While a paused session is being put back, it reads as still loading.
    catalog: pause.isResuming ? { ...catalog, isLoadingQuestions: true } : catalog,
    session,
    pause,
    recorder,
    quality,
    isSubmitting,
    startSession,
    submitCurrent,
    discardRecording,
    startRecording,
    finish: () => setup.setStep('complete'),
  };
}
